import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Paper,
  Alert,
  Chip,
  LinearProgress,
  IconButton,
  Collapse,
} from '@mui/material'
import {
  DragIndicator as DragIndicatorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  School as SchoolIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Security as SecurityIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { apiService } from '../services/api'
import { useAuth } from '../contexts/AuthContext'

interface SystemFeature {
  id: string
  featureKey: string
  featureName: string
  description?: string
  category: string
  route?: string
  isActive: boolean
  sortOrder: number
  roleFeatures: RoleFeature[]
}

interface RoleFeature {
  id: string
  role: string
  systemFeatureId: string
  isEnabled: boolean
  systemFeature: SystemFeature
}

interface RolePermissions {
  [role: string]: RoleFeature[]
}

// メニューカテゴリの定義（Layout.tsxの構造に合わせる）
interface MenuCategory {
  id: string
  label: string
  icon: React.ReactNode
  routes: string[] // このカテゴリに属するルート
  color: string
}

const menuCategories: MenuCategory[] = [
  {
    id: 'dashboard',
    label: 'ダッシュボード',
    icon: <DashboardIcon />,
    routes: ['/dashboard'],
    color: '#1F3A5F',
  },
  {
    id: 'basicFeatures',
    label: '基本機能',
    icon: <DashboardIcon />,
    routes: ['/my-enrollments'],
    color: '#2C6EBE',
  },
  {
    id: 'learningPlanning',
    label: '学習計画',
    icon: <SchoolIcon />,
    routes: ['/learning-plans', '/compliance', '/test-schedules', '/tests', '/reminder-manage'],
    color: '#1F3A5F',
  },
  {
    id: 'systemManagement',
    label: 'システム管理',
    icon: <AdminPanelSettingsIcon />,
    routes: ['/users', '/email-templates', '/email-delivery', '/subscription', '/invoices'],
    color: '#2C6EBE',
  },
  {
    id: 'superAdminManagement',
    label: 'スーパー管理者専用',
    icon: <SecurityIcon />,
    routes: ['/tenant-management', '/role-features', '/billing'],
    color: '#1F3A5F',
  },
]

// 機能をカテゴリに分類する関数
const categorizeFeature = (feature: SystemFeature): string => {
  const route = feature.route || ''
  for (const category of menuCategories) {
    if (category.routes.includes(route)) {
      return category.id
    }
  }
  return 'other'
}

interface SortableRowProps {
  feature: SystemFeature
  roles: string[]
  getPermissionForRoleFeature: (role: string, featureId: string) => boolean
  handlePermissionChange: (role: string, featureId: string, isEnabled: boolean) => void
}

const SortableRow: React.FC<SortableRowProps> = ({
  feature,
  roles,
  getPermissionForRoleFeature,
  handlePermissionChange,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: feature.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#f5f5f5' : 'inherit',
  }

  return (
    <TableRow ref={setNodeRef} style={style} {...attributes}>
      <TableCell sx={{ cursor: 'move', width: 50 }} {...listeners}>
        <IconButton size="small">
          <DragIndicatorIcon />
        </IconButton>
      </TableCell>
      <TableCell>
        <Box>
          <Typography variant="subtitle2">{feature.featureName}</Typography>
          {feature.description && (
            <Typography variant="caption" color="textSecondary">
              {feature.description}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="textSecondary">
          {feature.route || 'N/A'}
        </Typography>
      </TableCell>
      {roles.map((role) => (
        <TableCell key={role} align="center">
          <Switch
            checked={getPermissionForRoleFeature(role, feature.id)}
            onChange={(e) => handlePermissionChange(role, feature.id, e.target.checked)}
            color="primary"
          />
        </TableCell>
      ))}
    </TableRow>
  )
}

interface CategorySectionProps {
  category: MenuCategory
  features: SystemFeature[]
  roles: string[]
  getPermissionForRoleFeature: (role: string, featureId: string) => boolean
  handlePermissionChange: (role: string, featureId: string, isEnabled: boolean) => void
  onDragEnd: (event: DragEndEvent, categoryId: string) => void
  activeId: string | null
  sensors: ReturnType<typeof useSensors>
}

const CategorySection: React.FC<CategorySectionProps> = ({
  category,
  features,
  roles,
  getPermissionForRoleFeature,
  handlePermissionChange,
  onDragEnd,
  activeId,
  sensors,
}) => {
  const [expanded, setExpanded] = useState(true)

  const handleDragEnd = (event: DragEndEvent) => {
    onDragEnd(event, category.id)
  }

  if (features.length === 0) return null

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ pb: expanded ? 2 : '16px !important' }}>
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: 'pointer', mb: expanded ? 2 : 0 }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: 1,
              backgroundColor: category.color,
              color: 'white',
              mr: 2,
            }}
          >
            {category.icon}
          </Box>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {category.label}
          </Typography>
          <Chip label={`${features.length}件`} size="small" sx={{ mr: 1 }} />
          <IconButton size="small">{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
        </Box>

        <Collapse in={expanded}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={50}></TableCell>
                    <TableCell>機能名</TableCell>
                    <TableCell>ルート</TableCell>
                    {roles.map((role) => (
                      <TableCell key={role} align="center" sx={{ minWidth: 100 }}>
                        {role === 'USER'
                          ? '一般ユーザー'
                          : role === 'CORPORATE_ADMIN'
                            ? '管理者'
                            : 'スーパー管理者'}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <SortableContext
                    items={features.map((f) => f.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {features.map((feature) => (
                      <SortableRow
                        key={feature.id}
                        feature={feature}
                        roles={roles}
                        getPermissionForRoleFeature={getPermissionForRoleFeature}
                        handlePermissionChange={handlePermissionChange}
                      />
                    ))}
                  </SortableContext>
                </TableBody>
              </Table>
            </TableContainer>
            <DragOverlay>
              {activeId &&
                (() => {
                  const feature = features.find((f) => f.id === activeId)
                  return feature ? (
                    <Paper sx={{ p: 1, backgroundColor: 'white', boxShadow: 3 }}>
                      <Typography variant="subtitle2">{feature.featureName}</Typography>
                    </Paper>
                  ) : null
                })()}
            </DragOverlay>
          </DndContext>
        </Collapse>
      </CardContent>
    </Card>
  )
}

export const RoleFeatureManagement: React.FC = () => {
  const { t } = useTranslation('roleFeature')
  const { user, refreshAccessibleFeatures } = useAuth()
  const [features, setFeatures] = useState<SystemFeature[]>([])
  const [rolePermissions, setRolePermissions] = useState<RolePermissions>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [isSorting, setIsSorting] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const roles = ['USER', 'CORPORATE_ADMIN', 'SUPER_ADMIN']

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const [featuresResponse, permissionsResponse] = await Promise.all([
        apiService.get<SystemFeature[]>('/role-features/features'),
        apiService.get<RolePermissions>('/role-features/role-permissions'),
      ])

      setFeatures(featuresResponse)
      setRolePermissions(permissionsResponse)
      setError(null)
    } catch (error) {
      console.error('データ読み込みエラー:', error)
      setError(t('messages.loadError'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    if (user?.role !== 'SUPER_ADMIN') {
      setError(t('messages.superAdminOnly'))
      setLoading(false)
      return
    }
    loadData()
  }, [user, t, loadData])

  const handlePermissionChange = async (role: string, featureId: string, isEnabled: boolean) => {
    try {
      await apiService.put(`/role-features/role-permissions/${role}/${featureId}`, { isEnabled })

      // 状態を更新
      setRolePermissions((prev) => {
        const newPermissions = { ...prev }
        if (!newPermissions[role]) {
          newPermissions[role] = []
        }

        const existingIndex = newPermissions[role].findIndex(
          (p) => p.systemFeature.id === featureId
        )

        if (existingIndex >= 0) {
          newPermissions[role][existingIndex].isEnabled = isEnabled
        } else {
          const feature = features.find((f) => f.id === featureId)
          if (feature) {
            newPermissions[role].push({
              id: `new-${Date.now()}`,
              role,
              systemFeatureId: featureId,
              isEnabled,
              systemFeature: feature,
            })
          }
        }

        return newPermissions
      })

      // 権限変更後にaccessibleFeaturesを再取得
      await refreshAccessibleFeatures()
    } catch (error) {
      console.error('権限更新エラー:', error)
      setError(t('messages.permissionUpdateError'))
    }
  }

  const getPermissionForRoleFeature = (role: string, featureId: string): boolean => {
    const rolePerms = rolePermissions[role] || []
    const permission = rolePerms.find((p) => p.systemFeature.id === featureId)
    return permission?.isEnabled || false
  }

  // カテゴリ別に機能をグループ化
  const groupedFeatures = menuCategories.reduce(
    (acc, category) => {
      const categoryFeatures = features
        .filter((f) => categorizeFeature(f) === category.id)
        .sort((a, b) => a.sortOrder - b.sortOrder)
      acc[category.id] = categoryFeatures
      return acc
    },
    {} as Record<string, SystemFeature[]>
  )

  // 未分類の機能
  const uncategorizedFeatures = features
    .filter((f) => categorizeFeature(f) === 'other')
    .sort((a, b) => a.sortOrder - b.sortOrder)

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleCategoryDragEnd = async (event: DragEndEvent, categoryId: string) => {
    const { active, over } = event
    setActiveId(null)

    if (over && active.id !== over.id) {
      setIsSorting(true)

      // このカテゴリの機能リストを取得
      const categoryFeatures = groupedFeatures[categoryId] || []
      const oldIndex = categoryFeatures.findIndex((f) => f.id === active.id)
      const newIndex = categoryFeatures.findIndex((f) => f.id === over.id)

      if (oldIndex === -1 || newIndex === -1) {
        setIsSorting(false)
        return
      }

      // カテゴリ内で並び替え
      const reorderedCategoryFeatures = arrayMove(categoryFeatures, oldIndex, newIndex)

      // 全機能リストを更新（このカテゴリの機能のsortOrderを更新）
      const newFeatures = features.map((f) => {
        const indexInCategory = reorderedCategoryFeatures.findIndex((cf) => cf.id === f.id)
        if (indexInCategory !== -1) {
          // カテゴリ内の基準sortOrder + カテゴリ内インデックス
          const baseSortOrder = menuCategories.findIndex((c) => c.id === categoryId) * 100
          return { ...f, sortOrder: baseSortOrder + indexInCategory }
        }
        return f
      })

      setFeatures(newFeatures)

      try {
        // バックエンドに新しい順序を保存
        await apiService.put('/role-features/features/reorder', {
          features: newFeatures.map((f) => ({ id: f.id, sortOrder: f.sortOrder })),
        })
      } catch (error) {
        console.error('順序更新エラー:', error)
        setError(t('messages.orderUpdateError'))
        // エラー時はデータを再読み込み
        await loadData()
      } finally {
        setIsSorting(false)
      }
    }
  }

  if (user?.role !== 'SUPER_ADMIN') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('messages.accessDenied')}</Alert>
      </Box>
    )
  }

  if (loading) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            {t('title')}
          </Typography>
        </Box>
        <LinearProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* タイトル行 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('title')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 概要統計 */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              {t('stats.totalFeatures')}
            </Typography>
            <Typography variant="h5">{features.length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              {t('stats.activeFeatures')}
            </Typography>
            <Typography variant="h5">{features.filter((f) => f.isActive).length}</Typography>
          </CardContent>
        </Card>
      </Box>

      {isSorting && <LinearProgress sx={{ mb: 2 }} />}

      {/* カテゴリ別機能管理 */}
      <DndContext sensors={sensors} onDragStart={handleDragStart}>
        {menuCategories.map((category) => (
          <CategorySection
            key={category.id}
            category={category}
            features={groupedFeatures[category.id] || []}
            roles={roles}
            getPermissionForRoleFeature={getPermissionForRoleFeature}
            handlePermissionChange={handlePermissionChange}
            onDragEnd={handleCategoryDragEnd}
            activeId={activeId}
            sensors={sensors}
          />
        ))}

        {/* 未分類の機能 */}
        {uncategorizedFeatures.length > 0 && (
          <CategorySection
            category={{
              id: 'other',
              label: 'その他',
              icon: <DashboardIcon />,
              routes: [],
              color: '#757575',
            }}
            features={uncategorizedFeatures}
            roles={roles}
            getPermissionForRoleFeature={getPermissionForRoleFeature}
            handlePermissionChange={handlePermissionChange}
            onDragEnd={handleCategoryDragEnd}
            activeId={activeId}
            sensors={sensors}
          />
        )}
      </DndContext>
    </Box>
  )
}

export default RoleFeatureManagement
